import { v4 as uuidv4 } from 'uuid';
import { PartialDeep } from 'type-fest';
import { AxiosError } from 'axios';
import { NetworkLabel } from '~/constants/network-lables';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import {
    IEvent,
    IFlow,
    TBlockType,
    IFlowPermission
} from '~/types/model.types';
import {
    IConnectedWorkflowsResponse,
    IEmbeddedLeadFormScriptResponse,
    IFlowApplyActionResponse,
    IFlowLastActionNumberResponse,
    IFlowPrePublishResponse,
    IFlowResponse,
    IFlowRevisionsFromBackend,
    IFlowUpdatedResponse,
    IPaymentVariablePendingMarkAsPaid,
    IPaymentVariablePendingMarkAsRefunded,
    IRecurringInvoiceResponse,
    IRequestPayment,
    IResetViewOnlyLinkResponse,
    TFlowSource,
    IRefundData,
    GetRefundDataRequest,
    IMarkPaymentAsInstantPayoutResponse,
    ICompanyResponse
} from '~/types/server.types';
import { DEFAULT_RETRY_CONFIG } from '~/services/base-api.service';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { TImportPDFFileType } from '~/components/CreateFlow/types/create-flow.types';

export interface IFetchFlowOptions {
    preview: boolean;
    active: boolean;
    regainControlFingerprint: string;
}

export const FetchFlowOptionsDefaults = {
    preview: false,
    active: false,
    regainControlFingerprint: null
};

export class FlowAdapter extends BaseHttpAdapter {
    async _fetchFlow(
        flowId: string,
        {
            preview = FetchFlowOptionsDefaults.preview,
            active = FetchFlowOptionsDefaults.active,
            regainControlFingerprint
        }: IFetchFlowOptions = FetchFlowOptionsDefaults
    ): Promise<IFlowResponse> {
        const url = preview
            ? `/api/v2/flow/${flowId}/preview`
            : `/api/v2/flow/${flowId}${active ? '/active' : ''}?cb=${uuidv4()}`;

        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url,
            params: {
                fingerprint: regainControlFingerprint
            }
        });
    }

    // Used only for playground
    async createFlow() {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: '/api/v2/flow'
        });
    }

    async createFlowTemplate(flowId: string, title: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: '/api/v2/flow',
            data: { title, flow_id: flowId }
        });
    }

    async updateFlow(id: string, data: PartialDeep<IFlow>) {
        // Right now we're not doing anything with the response
        // I implemented my own "optimistic update" here,
        // This needs to be merged with Nirel's branch
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'put',
            url: `/api/v2/flow/${id}`,
            data
        });
    }

    async markFlowAsCompleted(id: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'put',
            url: `/api/v2/flow/${id}/complete`
        });
    }

    async removeScheduleSend(id: string) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/flow/${id}/remove_schedule_send`
        });
    }

    async deleteFlow(id: string) {
        return this.apiService.apiRequest({
            method: 'delete',
            url: `/api/v2/flow/${id}`
        });
    }

    async getConnectedWorkflows(id: string) {
        return this.apiService.apiRequest<IConnectedWorkflowsResponse>({
            method: 'get',
            url: `/api/v2/flow/${id}/workflows`
        });
    }

    async _getFlowRevisions(id: string) {
        return this.apiService.apiRequest<IFlowRevisionsFromBackend>({
            method: 'get',
            url: `/api/v2/flow/${id}/revisions`
        });
    }

    async _getFlowByRevision(flowId: string, revisionId: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/revision/${revisionId}`
        });
    }

    async getLinkedAutomationsPerFlow(flow_id: string) {
        return this.apiService.apiRequest<any>({
            method: 'get',
            url: `/api/v2/flow/${flow_id}/linked_workflows`
        });
    }

    async _mergeFlowRevisions(id: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: `/api/v2/flow/${id}/merge_revisions/`
        });
    }

    async prePublish(id: string) {
        return this.apiService.apiRequest<IFlowPrePublishResponse>({
            method: 'get',
            url: `/api/v2/flow/${id}/pre_publish/`
        });
    }

    async updateFlowConfiguration(
        flowId: string,
        configuration: FlowConfigurationModel
    ) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/configuration`,
            data: configuration
        });
    }

    async resetFlowSlugs(flowId: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/public/${flowId}/delete_slugs`
        });
    }

    async resetViewOnlyLink(flowId: string) {
        return this.apiService.apiRequest<IResetViewOnlyLinkResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/reset_view_only_link`
        });
    }

    async toggleViewOnlyLink(flowId: string, enabled: boolean) {
        return this.apiService.apiRequest<IResetViewOnlyLinkResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/view_only_link`,
            data: { viewer_link_sharing_enabled: !!enabled }
        });
    }

    async publishFlow(id: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: `/api/v2/flow/${id}/publish/`
        });
    }

    async _discardDraft(id: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'delete',
            url: `/api/v2/flow/${id}/draft`
        });
    }

    async markAsPaid(
        data: IPaymentVariablePendingMarkAsPaid,
        flowId: string,
        paymentId: string
    ): Promise<any> {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/paid`,
            data
        });
    }

    async requestPayment({
        paymentId,
        flowId,
        email
    }: IRequestPayment): Promise<IFlowUpdatedResponse> {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/request_payment`,
            data: { email }
        });
    }

    async undoMarkAsPaid(flowId: string, paymentId: string) {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/undo_paid`
        });
    }

    async getPaymentConnectedInvoice(flowId: string, paymentId: string) {
        return this.apiService.apiRequest<IRecurringInvoiceResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/payment_invoice/${paymentId}`,
            params: {}
        });
    }

    async getInvoiceByPaymentId(flowId: string, paymentId: string) {
        return this.apiService.apiRequest<IRecurringInvoiceResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/payment_invoice_by_payment_id/${paymentId}`,
            params: {}
        });
    }

    async markAsRefund(
        data: IPaymentVariablePendingMarkAsRefunded,
        flowId: string,
        paymentId: string
    ) {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/mark_refunded`,
            data
        });
    }

    async getRefundData(companyId: string, params: GetRefundDataRequest) {
        return this.apiService.apiRequest<IRefundData>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/refund_payment_data`,
            params
        });
    }

    async setFlowPermissions(flowId: string, permissions: IFlowPermission[]) {
        return this.apiService.apiRequest<{
            flow_permissions: IFlowPermission[];
        }>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/flow_permissions`,
            data: { permissions }
        });
    }

    async getFlowPermissions(flowId: string) {
        return this.apiService.apiRequest<{
            flow_permissions: IFlowPermission[];
        }>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/flow_permissions`
        });
    }

    async sendReminder(flowId: string, paymentId: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/reminder`
        });
    }

    async signWithoutSubmission(flowId: string, signature_variable: Object) {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/sign`,
            data: {
                signature_variable
            }
        });
    }

    async getFlowLastActionNumber(
        flowId: string
    ): Promise<IFlowLastActionNumberResponse> {
        return this.apiService.apiRequest<IFlowLastActionNumberResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/last_action_number?cb=${uuidv4()}`
        });
    }

    async stopRecurringInvoice(flowId: string, data) {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/stop`,
            data
        });
    }

    async markPaymentAsInstantPayout({
        paymentParentId,
        paymentId,
        source,
        newState = true
    }: {
        paymentParentId: string;
        paymentId: string;
        source: string;
        newState: boolean;
    }): Promise<IMarkPaymentAsInstantPayoutResponse> {
        return this.apiService.apiRequest({
            data: {
                use_instant_payout: newState,
                use_instant_payout_set_source: source
            },
            method: 'put',
            url: `/api/v2/flows/${paymentParentId}/payments/${paymentId}/instant_payout`
        });
    }

    async setAutomaticInstantPayout(
        companyId: string,
        isAutomaticInstantPayoutEnabled: boolean
    ): Promise<boolean> {
        const { all_inclusive_instant_payouts } =
            await this.apiService.apiRequest<Partial<ICompanyResponse>>({
                method: 'put',
                data: {
                    all_inclusive_instant_payouts:
                        isAutomaticInstantPayoutEnabled,
                    all_inclusive_instant_payouts_source:
                        'instant_deposit_modal_universal_toggle'
                },
                url: `/api/v2/companies/${companyId}/instant_payouts`
            });
        return all_inclusive_instant_payouts;
    }

    protected async _applyActions({
        flowId,
        actions,
        fingerprint,
        sessionId: session_id,
        wasWebsocketActive: was_websocket_active,
        onRetryAttempt,
        shouldRetry
    }: {
        flowId: string;
        actions;
        sessionId: string;
        fingerprint: string;
        wasWebsocketActive: boolean;
        onRetryAttempt: (err: AxiosError) => void;
        shouldRetry: (err: AxiosError) => boolean;
    }): Promise<IFlowApplyActionResponse> {
        try {
            return await this.apiService.apiRequest<IFlowApplyActionResponse>({
                method: 'put',
                url: `/api/v2/flow/${flowId}/apply_actions`,
                data: {
                    actions,
                    session_id,
                    fingerprint,
                    was_websocket_active
                },
                networkLabel: NetworkLabel.FlowActions,
                raxConfig: {
                    // Retry on network errors config
                    ...DEFAULT_RETRY_CONFIG,
                    httpMethodsToRetry: ['GET', 'PUT'],
                    // shouldRetry is not async so in order to validate if we should retry we are using an async function onRetryAttempt in order to do so because onRetryAttempt support async function.
                    onRetryAttempt: onRetryAttempt,
                    shouldRetry: shouldRetry,
                    retryDelay: 500 // we want to give the server a chance to process the apply action.
                }
            });
        } catch (error) {
            const data = error?.response?.data || error?.data;
            const errorType = data?.error_type;

            // If we were able to recover the server response we want to return it as the result of the apply actions.
            if (errorType === 'HBActionSuccessInServerWithoutResponse') {
                return Promise.resolve(data.recoveredResponse);
            }

            return Promise.reject(error);
        }
    }

    async recoverFlowResponse(
        flowId: string,
        actions
    ): Promise<IFlowApplyActionResponse> {
        return this.apiService.apiRequest<IFlowApplyActionResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/recover`,
            data: { actions },
            raxConfig: {
                // Retry on network errors config
                ...DEFAULT_RETRY_CONFIG,
                retry: 3,
                httpMethodsToRetry: ['GET', 'PUT']
            }
        });
    }

    createFlowFromPDF(data: {
        pdf_s3_url: string;
        pdf_file_name: string;
        flow_types: TImportPDFFileType[];
        workspace_id?: string;
        event?: IEvent;
        extra_blocks?: TBlockType[];
    }) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/flow_from_pdf/instance`,
            data
        });
    }

    createOnboardingFlowFromPDF(data: {
        pdf_s3_url: string;
        pdf_file_name: string;
        flow_types: TImportPDFFileType[];
        extra_blocks?: TBlockType[];
    }) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/flow_from_pdf/onb_instance`,
            data
        });
    }

    sendTestFlowFromOnboarding(templateId: string, userId: string) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'put',
            url: `/api/v2/users/${userId}/onboarding_sequence/send_test_flow_from_setup_guide`,
            data: {
                filters: { id: [templateId] },
                flow_source: 'send_test_setup_guide'
            }
        });
    }

    // #region LeadForm
    createPublicFlow({
        flowId,
        flowSource
    }: {
        flowId?: string;
        flowSource?: TFlowSource;
    }) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: '/api/v2/public/public_flow',
            data: { flow_id: flowId, flow_source: flowSource }
        });
    }

    deactivatePublicFlow(id: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/public/${id}/deactivate`
        });
    }

    activatePublicFlow(id: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/public/${id}/activate`
        });
    }

    deletePublicFlow(id: string) {
        return this.apiService.apiRequest({
            method: 'delete',
            url: `/api/v2/public/${id}`
        });
    }

    getEmbeddedFormScript(flowId: string) {
        return this.apiService.apiRequest<IEmbeddedLeadFormScriptResponse>({
            method: 'get',
            url: `/api/v2/public/${flowId}/embed`
        });
    }

    // #endregion
}
